import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useBusinessProfileData } from '../../hooks/useBusinessProfileData';
import { ProductsPanel } from '../Products/ProductsPanel';
import { ConsolidatedInvoicingModal } from '../Invoicing/ConsolidatedInvoicingModal';
import { MonthlyReportModal } from '../Reports/MonthlyReportModal';
import { SellServiceModal } from '../SellServiceModal';
import { SalesOverviewPanel } from '../SalesOverviewPanel';

// "Operacje" tab — the day-to-day work surface. Each tile opens its panel
// in one click. Row 1: frequent work (sell, sales overview, services);
// row 2: occasional (consolidated invoicing, monthly reports). One-off setup
// and the business's relationship with Spotto live in ConfigurationPage.

type Panel = 'sell' | 'sales' | 'products' | 'invoicing' | 'reports';

interface TileProps {
  icon: ReactNode;
  title: string;
  description: string;
  footer?: ReactNode;
  onClick: () => void;
}

// Same dark container + hover as the §4.8 section headers.
function OperationTile({ icon, title, description, footer, onClick }: TileProps) {
  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onClick}
        className="w-full h-full text-left p-5 flex flex-col gap-3 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary-light"
      >
        {icon}
        <span className="text-lg font-medium text-white">{title}</span>
        <span className="text-sm text-white/60">{description}</span>
        {footer}
      </button>
    </div>
  );
}

export default function OperationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const {
    isLoading,
    profile,
    fullProfile,
    isNewProfile,
    facilities,
    loadError,
    loadData,
  } = useBusinessProfileData();

  const [openPanel, setOpenPanel] = useState<Panel | null>(null);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="h-8 bg-surface-card rounded w-64 animate-pulse mb-2" />
          <div className="h-5 bg-surface-app-elevated rounded w-96 animate-pulse" />
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-surface-app-elevated rounded-lg border border-border-strong p-4">
              <div className="h-6 bg-surface-card rounded w-48 animate-pulse mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-10 bg-surface-card rounded animate-pulse" />
                <div className="h-10 bg-surface-card rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-brand-warm/30 border border-brand-warm text-white px-6 py-8 rounded-lg text-center">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-semibold mb-2">{t('common.error')}</h2>
          <p className="mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="px-6 py-2 bg-brand-warm hover:bg-brand-warm-deep text-white rounded-lg transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Defensive — shouldn't be reachable in practice since the profile-setup
  // gate runs before any nav tab (including this one) is visible.
  if (isNewProfile || !profile?.id) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-surface-card border border-border-subtle rounded-xl p-6 text-center">
          <p className="text-ink-secondary mb-4">
            {t('settingsPage.configurationNeedsProfile', 'Najpierw uzupełnij dane firmy w Konfiguracji, w sekcji Firma.')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/configuration')}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors"
          >
            {t('navigation.configuration')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-primary">
          {t('navigation.operations')}
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <OperationTile
          icon={
            <svg className="w-6 h-6 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          title={t('purchases.issueCompTitle', 'Sprzedaż usług')}
          description={t('purchases.issueCompHint', 'Zarejestruj usługę zakupioną w obiekcie (opłata na miejscu).')}
          onClick={() => setOpenPanel('sell')}
        />

        <OperationTile
          icon={
            <svg className="w-6 h-6 text-brand-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
          title={t('operationsPage.salesOverviewTitle', 'Przegląd sprzedaży')}
          description={t('operationsPage.salesOverviewDescription', 'Zakupy klientów: wyszukiwanie, oznaczanie wejść i przedłużanie ważności.')}
          onClick={() => setOpenPanel('sales')}
        />

        <OperationTile
          icon={
            <svg className="w-6 h-6 text-brand-confirm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
          title={t('operationsPage.servicesTitle', 'Zarządzanie usługami')}
          description={t('operationsPage.servicesDescription', 'Dodawaj, edytuj i porządkuj usługi w sklepie.')}
          onClick={() => setOpenPanel('products')}
        />

        <OperationTile
          icon={
            <svg className="w-6 h-6 text-brand-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
            </svg>
          }
          title={t('invoicing.title', 'Fakturowanie zbiorcze')}
          description={t('operationsPage.invoicingDescription', 'Łącz transakcje klientów w jedną fakturę.')}
          footer={
            fullProfile?.consolidatedInvoicingEnabled ? (
              <span className="self-start px-2 py-0.5 rounded-full text-xs bg-brand-confirm text-white">
                {t('invoicing.activeLabel', 'Aktywne')}
              </span>
            ) : (
              <span className="text-xs text-white/60">
                {t(
                  'invoicing.disabledHint',
                  'Fakturowanie zbiorcze jest wyłączone. Włączysz je w Konfiguracji, w sekcji KSeF.'
                )}
              </span>
            )
          }
          onClick={() => setOpenPanel('invoicing')}
        />

        <OperationTile
          icon={
            <svg className="w-6 h-6 text-brand-confirm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          title={t('reports.title', 'Raporty miesięczne')}
          description={t('operationsPage.reportsDescription', 'Przychody, rezerwacje i zakupy usług w wybranym miesiącu.')}
          onClick={() => setOpenPanel('reports')}
        />
      </div>

      {openPanel === 'sell' && (
        <SellServiceModal
          businessProfileId={profile.id}
          onClose={() => setOpenPanel(null)}
          onSuccess={() => showToast('success', t('operationsPage.sellSuccess', 'Sprzedaż zapisana'))}
        />
      )}

      {openPanel === 'sales' && <SalesOverviewPanel onClose={() => setOpenPanel(null)} />}

      {openPanel === 'products' && (
        <ProductsPanel
          businessProfileId={profile.id}
          facilities={facilities}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'invoicing' && (
        <ConsolidatedInvoicingModal
          businessProfileId={profile.id}
          isOwner={!!user && user.id === fullProfile?.userId}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'reports' && (
        <MonthlyReportModal
          businessProfileId={profile.id}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {/* Version stamp */}
      <div className="mt-6 text-right text-[10px] text-ink-muted tracking-wider font-mono select-all">
        v{__APP_VERSION__}
      </div>
    </div>
  );
}
